import { useEffect, useRef, useState } from "react";
import { DailyItemType, PlannerDatabaseType } from "../types/Planner";
import { GeneralObject } from "../types/General";
import { dayInMilliseconds, getDayIndex, ISOStringWithTimeZone } from "../helpers/DateAndTimeHelpers";

function useTestAndTimeSum(data: PlannerDatabaseType, startIndex = 0) {    
    const [timeSum, setTimeSum] = useState(0);
    const [testSum, setTestSum] = useState(0);
    const [questionSum, setQuestionSum] = useState(0);

    const [dailyBasedStudentTimeSum, setDailyBasedStudentTimeSum] = useState<GeneralObject<number>>({});
    const [dailyBasedStudentTestSum, setDailyBasedStudentTestSum] = useState<GeneralObject<number>>({});
    const [dailyBasedStudentQuestionSum, setDailyBasedStudentQuestionSum] = useState<GeneralObject<number>>({});
    
    const [dailyBasedTestSum, setDailyBasedTestSum] = useState<GeneralObject<number>>({});
    const [dailyBasedTimeSum, setDailyBasedTimeSum] = useState<GeneralObject<number>>({});
    const [dailyBasedQuestionSum, setDailyBasedQuestionSum] = useState<GeneralObject<number>>({});

    const [lessonBasedTestSum, setLessonBasedTestSum] = useState<GeneralObject<number>>({});
    const [lessonBasedTimeSum, setLessonBasedTimeSum] = useState<GeneralObject<number>>({});
    const [lessonBasedQuestionSum, setLessonBasedQuestionSum] = useState<GeneralObject<number>>({});

    function dailySum(dayData: (DailyItemType | null)[], lessonBasedTestSum: GeneralObject<number>,
            lessonBasedStudySum: GeneralObject<number>, lessonBasedQuestionSum: GeneralObject<number>, ignoreLessonBase: boolean) {

        let testSum = 0, timeSum = 0, questionSum = 0, studentTestSum = 0, studentTimeSum = 0, studentQuestionSum = 0;

        dayData.forEach((item) => {
            if (item && item.metadata) {
                if (item.role === 1) {
                    if (!ignoreLessonBase) {
                        const currentStudySum = (lessonBasedStudySum[item.metadata.lesson.id] || 0);
                        const currentTestSum = (lessonBasedTestSum[item.metadata.lesson.id] || 0);
                        const currentQuestionSum = lessonBasedQuestionSum[item.metadata.lesson.id] || 0;

                        lessonBasedStudySum[item.metadata.lesson.id] = currentStudySum + item.metadata.plannedTime;
                        lessonBasedTestSum[item.metadata.lesson.id] = currentTestSum + item.metadata.plannedTest;
                        lessonBasedQuestionSum[item.metadata.lesson.id] = currentQuestionSum + (item.metadata?.plannedQuestions || 0);
                    }

                    timeSum += item.metadata.plannedTime;
                    testSum += item.metadata.plannedTest;
                    questionSum += item.metadata?.plannedQuestions || 0;
                }

                studentTimeSum += item.time || 0;
                studentQuestionSum += item.metadata?.questionsCount || 0;

                if (item.type === 1 || item.type === 4)
                    studentTestSum += (item.metadata.testCount || 0);
            }
        })

        return { 
            testSum: testSum, 
            timeSum: timeSum, 
            questionSum: questionSum, 
            studentTestSum: studentTestSum, 
            studentTimeSum: studentTimeSum,
            studentQuestionSum: studentQuestionSum
        }
    }

    useEffect(() => {
        let testSum = 0, timeSum = 0, questionSum = 0;
        let dailyBasedTestSum = {} as GeneralObject<number>;
        let dailyBasedTimeSum = {} as GeneralObject<number>;
        let dailyBasedQuestionSum = {} as GeneralObject<number>;
        
        let studentDailyBasedTestSum = {} as GeneralObject<number>;
        let studentDailyBasedTimeSum = {} as GeneralObject<number>;
        let studentDailyBasedQuestionSum = {} as GeneralObject<number>;

        let lessonBasedTestSum = {} as GeneralObject<number>;
        let lessonBasedTimeSum = {} as GeneralObject<number>;
        let lessonBasedQuestionSum = {} as GeneralObject<number>;

        const todayIndex = getDayIndex();
        const firstWeekOfDay = new Date(new Date().getTime() + ((startIndex - todayIndex) * dayInMilliseconds));
        const lastWeekOfDay = new Date(firstWeekOfDay.getTime() + ((startIndex === 0 ? 6 : 7) * dayInMilliseconds));

        const firstDayOfWeekISO = ISOStringWithTimeZone(firstWeekOfDay);
        const lastWeekOfDayISO = ISOStringWithTimeZone(lastWeekOfDay);

        Object.keys(data as PlannerDatabaseType).forEach((item) => {
            const sum = dailySum(data[item], lessonBasedTestSum, lessonBasedTimeSum, lessonBasedQuestionSum, firstDayOfWeekISO > item || lastWeekOfDayISO < item);

            dailyBasedTestSum[item] = sum.testSum;
            dailyBasedTimeSum[item] = sum.timeSum;
            dailyBasedQuestionSum[item] = sum.questionSum;

            studentDailyBasedTestSum[item] = sum.studentTestSum;
            studentDailyBasedTimeSum[item] = sum.studentTimeSum;
            studentDailyBasedQuestionSum[item] = sum.studentQuestionSum;

            if (firstDayOfWeekISO <= item && lastWeekOfDayISO >= item) {
                testSum += sum.testSum;
                timeSum += sum.timeSum;
                questionSum += sum.questionSum;
            }
        })

        setTestSum(testSum);
        setTimeSum(timeSum);
        setQuestionSum(questionSum);

        setDailyBasedTestSum(dailyBasedTestSum);
        setDailyBasedTimeSum(dailyBasedTimeSum);
        setDailyBasedQuestionSum(dailyBasedQuestionSum);
        
        setLessonBasedTestSum(lessonBasedTestSum);
        setLessonBasedTimeSum(lessonBasedTimeSum);
        setLessonBasedQuestionSum(lessonBasedQuestionSum);

        setDailyBasedStudentTestSum(studentDailyBasedTestSum);
        setDailyBasedStudentTimeSum(studentDailyBasedTimeSum);
        setDailyBasedStudentQuestionSum(studentDailyBasedQuestionSum);
    }, [data, startIndex])

    return {
        testSum,
        timeSum,
        questionSum,

        dailyBasedTestSum,
        dailyBasedTimeSum,
        dailyBasedQuestionSum,
        
        lessonBasedTimeSum,
        lessonBasedTestSum,
        lessonBasedQuestionSum,

        dailyBasedStudentTestSum,
        dailyBasedStudentTimeSum,
        dailyBasedStudentQuestionSum
    }
}

export default useTestAndTimeSum;